import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import { CircularProgress } from "@mui/material";

import { bulkJobsStore, isJobRunning, useBulkJobs } from "./bulkJobsStore";
import BulkJobDetailsDrawer from "./BulkJobDetailsDrawer";
import "./styles.scss";

const AUTO_DISMISS_MS = 10000;

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const StatusIcon = ({ status }) => {
  if (status === "queued" || status === "running") {
    return <CircularProgress size={16} thickness={5} className="bjt-icon bjt-icon--spin" />;
  }
  if (status === "completed") {
    return <CheckCircleOutlineIcon className="bjt-icon bjt-icon--ok" fontSize="small" />;
  }
  if (status === "completed_with_errors") {
    return <WarningAmberIcon className="bjt-icon bjt-icon--warn" fontSize="small" />;
  }
  if (status === "cancelled") {
    return <DoNotDisturbOnOutlinedIcon className="bjt-icon bjt-icon--muted" fontSize="small" />;
  }
  return <ErrorOutlineIcon className="bjt-icon bjt-icon--err" fontSize="small" />;
};

const JobRow = ({ job, onDetails }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const total = job.total || 0;
  const processed = Math.min(job.processed || 0, total);
  const percent = total ? Math.round((processed / total) * 100) : 0;
  const { updated = 0, unchanged = 0, skipped = 0, failed = 0 } = job.counts || {};

  // Completed cleanly → get out of the way after a moment.
  useEffect(() => {
    if (job.status !== "completed") return undefined;
    const t = setTimeout(() => bulkJobsStore.dismiss(job.jobId), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [job.status, job.jobId]);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const neck = job.source === "neck_labels";
  const dst = job.source === "dst_replace";
  const removing = job.type === "remove_neck_labels";
  const label =
    job.label ||
    (dst ? "Updating products" : neck ? "Updating neck labels" : "Assigning print methods");
  let title = label;
  let line = null;
  const actions = [];

  if (job.status === "queued") {
    line = "Waiting to start…";
  } else if (job.status === "running") {
    line = `${processed} of ${total}${failed ? ` · ${failed} failed` : ""}`;
    actions.push(
      job.cancelRequested ? (
        <span key="c" className="bjt-muted">Cancelling…</span>
      ) : (
        <button
          key="c"
          type="button"
          className="bjt-link"
          disabled={busy}
          onClick={() => run(() => bulkJobsStore.cancel(job.jobId))}
        >
          Cancel
        </button>
      )
    );
  } else if (job.status === "completed") {
    title = dst
      ? `DST updated on ${plural(updated, "product")}`
      : neck
        ? `${removing ? "Neck labels removed from" : "Neck labels updated on"} ${plural(updated, "item")}`
        : `Print methods updated on ${plural(updated, "product")}`;
    if (unchanged) {
      line = dst
        ? `${unchanged} no longer used it`
        : neck
          ? `${unchanged} kept their own label`
          : `${unchanged} already set this way`;
    }
  } else if (job.status === "completed_with_errors") {
    const parts = [`Updated ${updated}`];
    if (skipped) parts.push(`Skipped ${skipped}`);
    if (failed) parts.push(`Failed ${failed}`);
    title = label;
    line = parts.join(" · ");
    actions.push(
      <button key="d" type="button" className="bjt-link" onClick={() => onDetails(job)}>
        View details
      </button>
    );
    if (failed) {
      actions.push(
        <button
          key="r"
          type="button"
          className="bjt-link"
          disabled={busy}
          onClick={() => run(() => bulkJobsStore.retryFailed(job.jobId))}
        >
          Retry failed
        </button>
      );
    }
  } else if (job.status === "cancelled") {
    title = `Cancelled · ${updated} of ${total} updated`;
    actions.push(
      <button key="d" type="button" className="bjt-link" onClick={() => onDetails(job)}>
        View details
      </button>
    );
  } else {
    title = dst
      ? "Couldn't finish updating products"
      : neck
        ? "Couldn't finish updating neck labels"
        : "Couldn't finish updating print methods";
    line = processed ? `${processed} of ${total} done before it stopped` : null;
    actions.push(
      <button
        key="r"
        type="button"
        className="bjt-link"
        disabled={busy}
        onClick={() => run(() => bulkJobsStore.retryFailed(job.jobId))}
      >
        Retry
      </button>
    );
  }

  if (!isJobRunning(job)) {
    actions.push(
      <button
        key="x"
        type="button"
        className="bjt-link bjt-link--quiet"
        onClick={() => bulkJobsStore.dismiss(job.jobId)}
      >
        Dismiss
      </button>
    );
  }

  return (
    <div className={`bjt-row bjt-row--${job.status}`}>
      <StatusIcon status={job.status} />
      <div className="bjt-row__body">
        <div className="bjt-row__title">{title}</div>
        {isJobRunning(job) ? (
          <div
            className={`bjt-bar${job.status === "queued" ? " bjt-bar--indeterminate" : ""}`}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={job.status === "queued" ? undefined : percent}
          >
            <div
              className="bjt-bar__fill"
              style={job.status === "queued" ? undefined : { width: `${percent}%` }}
            />
          </div>
        ) : null}
        {line ? <div className="bjt-row__line">{line}</div> : null}
        {error ? <div className="bjt-row__error">{error}</div> : null}
        {actions.length ? <div className="bjt-row__actions">{actions}</div> : null}
      </div>
    </div>
  );
};

/**
 * Bottom-right card listing bulk jobs. Mounted once in App, so it follows the
 * user across pages and survives refreshes (jobs are loaded from the server).
 */
const BulkJobsTracker = () => {
  const isEnterpriseAdmin = useSelector(
    (state) => state?.currentUser?.user?.isEnterpriseAdmin
  );
  const uid = useSelector((state) => state?.currentUser?.user?.uid);
  const { jobs, minimized } = useBulkJobs();
  const [detailsJob, setDetailsJob] = useState(null);
  const cardRef = useRef(null);

  // Admins see print-method and neck-label jobs; sellers their neck-label jobs.
  useEffect(() => {
    if (!uid) return undefined;
    bulkJobsStore.load(uid, { isAdmin: Boolean(isEnterpriseAdmin) });
    return () => bulkJobsStore.stop();
  }, [isEnterpriseAdmin, uid]);

  const visible = Boolean(uid && jobs.length);

  // Let other bottom-right floats (send-to-channel progress) sit above us.
  useLayoutEffect(() => {
    const body = document.body;
    if (!visible || !cardRef.current) {
      body.classList.remove("has-bulk-jobs-tracker");
      return undefined;
    }
    body.classList.add("has-bulk-jobs-tracker");
    const setHeight = () =>
      body.style.setProperty(
        "--bulk-jobs-tracker-h",
        `${cardRef.current?.offsetHeight || 0}px`
      );
    setHeight();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(setHeight) : null;
    ro?.observe(cardRef.current);
    return () => {
      ro?.disconnect();
      body.classList.remove("has-bulk-jobs-tracker");
    };
  }, [visible, minimized, jobs.length]);

  if (!visible) return null;

  const running = jobs.filter(isJobRunning);
  const header =
    running.length > 1
      ? `${running.length} jobs running`
      : running.length === 1
        ? jobs.length === 1
          ? running[0].label?.split(" · ")[0] ||
            (running[0].source === "dst_replace"
              ? "Updating products"
              : running[0].source === "neck_labels"
                ? "Updating neck labels"
                : "Assigning print methods")
          : "1 job running"
        : jobs.length === 1
          ? "Bulk update finished"
          : `${jobs.length} bulk updates`;

  return (
    <>
      <div
        ref={cardRef}
        className={`bjt${minimized ? " bjt--min" : ""}`}
        role="status"
        aria-live="polite"
      >
        <button
          type="button"
          className="bjt-head"
          onClick={() => bulkJobsStore.setMinimized(!minimized)}
          aria-expanded={!minimized}
        >
          {running.length ? (
            <CircularProgress size={14} thickness={5} className="bjt-icon bjt-icon--spin" />
          ) : null}
          <span className="bjt-head__title">{header}</span>
          {minimized ? (
            <ExpandLessIcon fontSize="small" aria-label="Show bulk updates" />
          ) : (
            <ExpandMoreIcon fontSize="small" aria-label="Minimize" />
          )}
        </button>
        {!minimized ? (
          <div className="bjt-list">
            {jobs.map((job) => (
              <JobRow key={job.jobId} job={job} onDetails={setDetailsJob} />
            ))}
          </div>
        ) : null}
      </div>
      <BulkJobDetailsDrawer job={detailsJob} onClose={() => setDetailsJob(null)} />
    </>
  );
};

export default BulkJobsTracker;
